import { apiClient } from '@/api/client'
import {
  companyPayrollLivePath,
  companyPayrollEmployeeLivePath,
  PAYROLL_CALCULATE,
  PAYROLL_CALCULATE_SLIP_PREVIEW_HTML,
  PAYROLL_ME_LIVE,
  PAYROLL_PAYMENTS,
  PAYROLL_PAY,
  payrollPaymentItemPdfPath,
  payrollPaymentPath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { PaginatedResponse } from '@/types/common.types'
import type {
  CompanyLiveResponse,
  PayrollCalculateRequest,
  PayrollPaymentDetail,
  PayrollPaymentSnapshot,
  PayrollPayRequest,
  PayrollPaymentResult,
  PayrollSelectionPreview,
  LiveSalaryResponse,
  MyLiveResponse,
} from '@/features/payroll/types/payroll.types'

export async function getCompanyLive(
  companyId: string,
): Promise<CompanyLiveResponse> {
  const { data } = await apiClient.get<ApiResponseWrapper<CompanyLiveResponse>>(
    companyPayrollLivePath(companyId),
  )
  return data.data
}

export async function getEmployeeLive(
  companyId: string,
  employeeId: string,
): Promise<LiveSalaryResponse> {
  const { data } = await apiClient.get<ApiResponseWrapper<LiveSalaryResponse>>(
    companyPayrollEmployeeLivePath(companyId, employeeId),
  )
  return data.data
}

export async function getMyLive(): Promise<MyLiveResponse> {
  const { data } = await apiClient.get<ApiResponseWrapper<MyLiveResponse>>(
    PAYROLL_ME_LIVE,
  )
  return data.data
}

export async function calculatePayrollPreview(
  body: PayrollCalculateRequest,
): Promise<PayrollSelectionPreview> {
  const { data } = await apiClient.post<ApiResponseWrapper<PayrollSelectionPreview>>(
    PAYROLL_CALCULATE,
    body,
  )
  return data.data
}

export async function getPayrollPreviewSlipHtml(
  body: PayrollCalculateRequest,
): Promise<Blob> {
  const { data } = await apiClient.post<Blob>(
    PAYROLL_CALCULATE_SLIP_PREVIEW_HTML,
    body,
    { responseType: 'blob' },
  )
  return data
}

export async function payPayrollSelection(
  body: PayrollPayRequest,
): Promise<PayrollPaymentResult> {
  const { data } = await apiClient.post<ApiResponseWrapper<PayrollPaymentResult>>(
    PAYROLL_PAY,
    body,
  )
  return data.data
}

export async function getPayrollPayments(params?: {
  page?: number
  limit?: number
  year?: number
  month?: number
}): Promise<PaginatedResponse<PayrollPaymentSnapshot>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedResponse<PayrollPaymentSnapshot>>>(
    PAYROLL_PAYMENTS,
    { params },
  )
  return data.data
}

export async function getPayrollPayment(
  snapshotId: string,
): Promise<PayrollPaymentDetail> {
  const { data } = await apiClient.get<ApiResponseWrapper<PayrollPaymentDetail>>(
    payrollPaymentPath(snapshotId),
  )
  return data.data
}

export async function downloadPayrollPaymentSlipPdf(
  snapshotId: string,
  itemId: string,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    payrollPaymentItemPdfPath(snapshotId, itemId),
    { responseType: 'blob' },
  )
  return data
}

/** Reverse a paid live payment. Reopens the employees' month; the reason is audited. */
export async function reversePayrollPayment(
  snapshotId: string,
  reason: string,
): Promise<PayrollPaymentSnapshot> {
  const { data } = await apiClient.post<ApiResponseWrapper<PayrollPaymentSnapshot>>(
    `${payrollPaymentPath(snapshotId)}/reverse`,
    { reason },
  )
  return data.data
}
